import { Grid } from '@mui/material';
import React, {
  ComponentProps,
  MouseEventHandler,
  useCallback,
  useState,
} from 'react';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import {
  IProjectColors,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { AlignEndColumn, Row } from 'spw-shared/globalComponents/grid';
import {
  asPercentageRounded,
  asUsf,
  getCustomSpaceColor,
  numberWithCommas,
  removeLineBreaks,
} from 'spw-shared/utils';
import { isNaN } from 'lodash';
import { MixBarHeader } from 'spw-programming-projects/program-summary/components/styles';
import {
  Category,
  IProjectSpaceType,
  ISpaceType,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import { SpaceIcon } from 'spw-projects/components/space-type-icons';
import { useDisableBulkEditButton } from 'spw-shared/hooks/use-disable-bulk-edit-button';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import WorksheetRow from '../../../../models/WorksheetRow';
import {
  BigKey,
  BoldValue,
  PercentBar,
  PercentBarContainer,
  NA,
} from '../macro-single-page-summary/styles';
import { spaceFactorMap as rawSpaceFactorMap } from '../macro-single-page-summary/utils';
import BulkEditDialog from '../program-summary-tabs/bulk-edit-dialog';
import { AvgUSFBulkEditButton } from './style';

const SpaceBar = ({
  percentage,
  color,
  long,
}: {
  percentage: number;
  color: string;
  long?: boolean;
}) => (
  <PercentBarContainer style={long ? { width: '232px' } : {}}>
    <PercentBar width={`${percentage * 100}%`} color={color} />
  </PercentBarContainer>
);

const BULK_EDIT_DIALOG_SUBJECT = 'USF';

export const UsfMixBar = ({
  row,
  category,
  subcategory,
  percentage,
  projectSpaceType,
  spaceType,
  total,
  averageUSF,
  isMacro,
  detailedIndividualConfig,
  projectColors,
  shouldHideAvgUSF,
  testingPrefix,
  onClickSpaceIcon,
  projectSpaceTypes,
  viewOnly,
  clientFacingNamesAreVisible,
}: {
  row: WorksheetRow;
  category: Category | Subcategory;
  subcategory?: Subcategory;
  percentage: number | string;
  projectSpaceType?: Partial<IProjectSpaceType>;
  spaceType: ISpaceType;
  total: number;
  averageUSF?: number;
  isMacro: boolean;
  detailedIndividualConfig?: boolean;
  projectColors: IProjectColors;
  shouldHideAvgUSF?: boolean;
  testingPrefix: string;
  onClickSpaceIcon?: (e: Element) => void;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  viewOnly: boolean;
  clientFacingNamesAreVisible?: boolean;
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const [bulkEditDialogProps, setBulkEditDialogProps] = useState<
    Omit<ComponentProps<typeof BulkEditDialog>, 'clientFacingNamesAreVisible'>
  >();
  const _disableBulkEditButton = useDisableBulkEditButton({
    row,
    subject: BULK_EDIT_DIALOG_SUBJECT,
  });
  const disableBulkEditButton =
    _disableBulkEditButton || detailedIndividualConfig;

  const handleBulkEditDialogClose = useCallback(() => {
    setBulkEditDialogProps(undefined);
  }, []);

  if (!spaceType) return null;

  const rowIcon =
    projectSpaceTypes[projectSpaceType?.id]?.icon || spaceType.icon;

  const spaceFactorMap = rawSpaceFactorMap(projectColors);
  let factors = spaceFactorMap[spaceType.spaceType];

  const wasSpaceTypeReassigned =
    factors && category !== factors.defaultCategory;
  if (wasSpaceTypeReassigned) factors.color = projectColors[category];
  const clientFacingName = projectSpaceType?.clientFacingName;
  if (!factors && spaceType?.name) {
    factors = {
      title: spaceType.name,
      color: getCustomSpaceColor(
        subcategory || category,
        spaceType.spaceType,
        projectColors
      ),
      defaultCategory: category,
    };
  }

  if (factors) {
    const { title, color } = factors;

    let totalDisplay = '0';
    if (isNaN(total)) {
      totalDisplay = NO_DATA_TO_SHOW;
    } else if (total > 0) {
      totalDisplay = numberWithCommas(
        Math.round(convertArea(total)),
        false,
        true
      );
    }

    const projectSpaceTypeCategory = projectSpaceType?.category;

    const handleAvgUsfBulkEditButtonClick = ((e) => {
      setBulkEditDialogProps({
        projectSpaceType,
        subject: BULK_EDIT_DIALOG_SUBJECT,
        anchorEl: e.currentTarget,
        onClose: handleBulkEditDialogClose,
      });
    }) as MouseEventHandler;

    return (
      <Grid container>
        <Grid item xs={6}>
          <div style={{ paddingBottom: '8px' }}>
            <MixBarHeader
              style={{ padding: '8px 0px 4px 0px' }}
              data-testid={`${testingPrefix}_spaceTypeName`}
              data-cy={`programming_ps_usf_mix_row_${spaceType.name}`}
            >
              <pre>
                {removeLineBreaks(
                  clientFacingName && clientFacingNamesAreVisible
                    ? clientFacingName
                    : projectSpaceType?.spaceType?.name?.toUpperCase()
                )}
              </pre>
            </MixBarHeader>
            <Row>
              <SpaceIcon
                disabled={viewOnly}
                projectSpaceTypeCategoryId={projectSpaceTypeCategory.id}
                topLevelProjectSpaceTypeCategoryId={
                  (projectSpaceTypeCategory as IProjectSpaceTypeSubcategory)
                    ?.parentId || projectSpaceTypeCategory.id
                }
                projectSpaceTypeId={projectSpaceType?.id}
                onClick={onClickSpaceIcon}
                iconName={rowIcon}
                defaultIconName={spaceType.icon}
                name={title}
              />
              <SpaceBar
                long={!isMacro}
                percentage={typeof percentage === 'number' ? percentage : 0}
                color={(color || projectColors[category]) as string}
              />
            </Row>
          </div>
        </Grid>
        <Grid item xs={6} style={{ display: 'flex' }}>
          <Grid container style={{ margin: '0 0 0' }}>
            <Grid item xs={4} style={{ paddingTop: '28px' }}>
              <AlignEndColumn>
                <BoldValue data-testid={`${testingPrefix}_percentage`}>
                  {asPercentageRounded(
                    typeof percentage === 'number' ? percentage : 0,
                    false
                  )}
                </BoldValue>
                <BigKey>{getUnit('area')}</BigKey>
              </AlignEndColumn>
            </Grid>
            <Grid
              item
              xs={4}
              style={{
                paddingRight: isMacro ? '' : '16px',
                paddingTop: '28px',
                paddingBottom: '8px',
              }}
            >
              <AlignEndColumn>
                <BoldValue data-testid={`${testingPrefix}_totalUSF`}>
                  {totalDisplay}
                </BoldValue>
                <BigKey>{getUnit('area')}</BigKey>
              </AlignEndColumn>
            </Grid>
            <Grid
              item
              xs={4}
              style={{
                borderLeft: !isMacro ? '1px solid #E0E0E0' : undefined,
                paddingTop: '28px',
                paddingBottom: '8px',
              }}
            >
              <div style={{ height: '32px' }}>
                <AvgUSFBulkEditButton
                  $disableHoverState={disableBulkEditButton}
                  className={bulkEditDialogProps ? 'selected' : ''}
                  onClick={
                    !disableBulkEditButton
                      ? handleAvgUsfBulkEditButtonClick
                      : undefined
                  }
                >
                  {!shouldHideAvgUSF && (
                    <AlignEndColumn>
                      {!detailedIndividualConfig && (
                        <BoldValue data-testid={`${testingPrefix}_averageUSF`}>
                          {averageUSF ? asUsf(convertArea(averageUSF)) : 0}
                        </BoldValue>
                      )}
                      {detailedIndividualConfig ? (
                        <NA style={{ marginTop: '0' }}>NA</NA>
                      ) : (
                        <BigKey>Avg {getUnit('area')}</BigKey>
                      )}
                    </AlignEndColumn>
                  )}
                </AvgUSFBulkEditButton>
                {bulkEditDialogProps && (
                  <BulkEditDialog
                    {...bulkEditDialogProps}
                    clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                  />
                )}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
  return null;
};
